/**
 * CacheInspector - Slick interface to view and manage cached words/text
 * 
 * Features:
 * - View cached words/phrases with beautiful UI
 * - Search and filter cached content
 * - Cache warming with common legal terms
 * - Real-time cache statistics
 * - Dark/light mode support
 * - Export cache data
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  FireIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  ClockIcon,
  HashtagIcon,
  LanguageIcon,
  AdjustmentsHorizontalIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface CacheEntry {
  key: string;
  text: string;
  fontFamily: string;
  fontSize: number;
  glyphCount: number;
  totalAdvance: number;
  cached: boolean;
  timestamp: string;
  script: string;
  language: string;
  direction: string;
}

interface CacheInspectorData {
  realmId: string;
  timestamp: string;
  totalCacheKeys: number;
  filteredEntries: number;
  searchTerm: string | null;
  entries: CacheEntry[];
  summary: {
    uniqueTexts: number;
    uniqueFonts: number;
    totalGlyphs: number;
    averageTextLength: string;
  };
}

interface CacheInspectorProps {
  className?: string;
}

// Common legal terms for cache warming
const LEGAL_TERMS_PRESETS = {
  'Common Legal Words': [
    'the', 'and', 'of', 'to', 'in', 'for', 'with', 'by', 'on', 'at',
    'plaintiff', 'defendant', 'court', 'case', 'law', 'legal', 'attorney',
    'contract', 'agreement', 'liability', 'damages', 'evidence', 'witness'
  ],
  'Legal Phrases': [
    'Whereas', 'Therefore', 'Notwithstanding', 'Furthermore', 'In consideration of',
    'Subject to', 'Provided that', 'In accordance with', 'With respect to',
    'For the purposes of', 'In the event that', 'To the extent that'
  ],
  'Court Documents': [
    'COMPLAINT', 'ANSWER', 'MOTION', 'ORDER', 'JUDGMENT', 'SUMMONS',
    'SUBPOENA', 'DEPOSITION', 'AFFIDAVIT', 'BRIEF', 'MEMORANDUM', 'EXHIBIT'
  ]
};

export const CacheInspector: React.FC<CacheInspectorProps> = ({ className = '' }) => {
  const [data, setData] = useState<CacheInspectorData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'text' | 'length'>('text');
  const [limit, setLimit] = useState(50);
  const [isWarming, setIsWarming] = useState(false);
  const [warmingResults, setWarmingResults] = useState<any>(null);
  const [selectedPreset, setSelectedPreset] = useState<string>('Common Legal Words');
  const [customTexts, setCustomTexts] = useState('');

  // Get current realm ID
  const getCurrentRealmId = useCallback((): string | null => {
    try {
      const token = localStorage.getItem('kc-token') || sessionStorage.getItem('kc-token');
      if (!token) return null;
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const realmMatch = payload.iss?.match(/\/realms\/([^\/]+)$/);
      return realmMatch ? realmMatch[1] : null;
    } catch (error) {
      return null;
    }
  }, []);

  // Fetch cache data
  const fetchCacheData = useCallback(async () => {
    let realmId = getCurrentRealmId();
    
    // Fallback to known realm ID for testing
    if (!realmId) {
      console.warn('[CacheInspector] Using fallback realm ID');
      realmId = 'edgelexlaw-realm'; // Fallback for testing
    }
    
    if (!realmId) {
      setError('Unable to determine realm ID');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        sort: sortBy
      });
      
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      const response = await fetch(`http://localhost:8081/cache/inspect?${params}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-realm-id': realmId
        }
      });

      if (response.ok) {
        const cacheData = await response.json();
        setData(cacheData);
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getCurrentRealmId, searchTerm, sortBy, limit]);

  // Cache warming function
  const warmCache = useCallback(async (texts: string[]) => {
    let realmId = getCurrentRealmId();
    
    // Fallback to known realm ID for testing
    if (!realmId) {
      console.warn('[CacheInspector] Using fallback realm ID for warming');
      realmId = 'edgelexlaw-realm'; // Fallback for testing
    }
    
    if (!realmId) {
      setError('Unable to determine realm ID');
      return;
    }

    setIsWarming(true);
    setWarmingResults(null);

    try {
      const response = await fetch('http://localhost:8081/cache/warm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-realm-id': realmId
        },
        body: JSON.stringify({
          texts,
          fontFamily: 'Times New Roman',
          fontSize: 16
        })
      });

      if (response.ok) {
        const results = await response.json();
        setWarmingResults(results);
        // Refresh cache data after warming
        setTimeout(fetchCacheData, 1000);
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
    } finally {
      setIsWarming(false);
    }
  }, [getCurrentRealmId, fetchCacheData]);

  // Handle preset warming
  const handlePresetWarming = useCallback(() => {
    const texts = LEGAL_TERMS_PRESETS[selectedPreset as keyof typeof LEGAL_TERMS_PRESETS];
    if (texts) {
      warmCache(texts);
    }
  }, [selectedPreset, warmCache]);

  // Handle custom text warming
  const handleCustomWarming = useCallback(() => {
    const texts = customTexts
      .split('\n')
      .map(t => t.trim())
      .filter(t => t.length > 0);
    
    if (texts.length > 0) {
      warmCache(texts);
    }
  }, [customTexts, warmCache]);

  // Export cache data
  const exportCacheData = useCallback(() => {
    if (!data) return;
    
    const exportData = {
      ...data,
      exportedAt: new Date().toISOString(),
      exportedBy: 'CacheInspector'
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cache-inspection-${data.realmId}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [data]);

  // Initial load
  useEffect(() => {
    fetchCacheData();
  }, [fetchCacheData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchCacheData, 30000);
    return () => clearInterval(interval);
  }, [fetchCacheData]);

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <EyeIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Cache Inspector
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                View and manage cached words and phrases
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={exportCacheData}
              disabled={!data}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              title="Export cache data"
            >
              <DocumentArrowDownIcon className="w-5 h-5 text-gray-500" />
            </button>
            <button
              onClick={fetchCacheData}
              disabled={loading}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh cache data"
            >
              <ArrowPathIcon className={`w-5 h-5 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Search and Controls */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search cached text..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchCacheData()}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-transparent"
            />
          </div>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'text' | 'length')}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
          >
            <option value="text">Sort by Text</option>
            <option value="length">Sort by Length</option>
          </select>
          
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
          >
            <option value={25}>Show 25</option>
            <option value={50}>Show 50</option>
            <option value={100}>Show 100</option>
            <option value={200}>Show 200</option>
          </select>
        </div>
      </div>

      {/* Cache Statistics */}
      {data && (
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-lex-accent">
                {data.totalCacheKeys.toLocaleString()}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Total Cached</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {data.summary.uniqueTexts.toLocaleString()}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Unique Texts</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {data.summary.uniqueFonts}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Font Variants</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {data.summary.averageTextLength}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Avg Length</div>
            </div>
          </div>
        </div>
      )}

      {/* Cache Warming Section */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2 mb-4">
          <FireIcon className="w-5 h-5 text-orange-500" />
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Cache Warming</h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Preset Warming */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Legal Terms Presets
            </label>
            <div className="flex space-x-2">
              <select
                value={selectedPreset}
                onChange={(e) => setSelectedPreset(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-lex-accent focus:border-transparent"
              >
                {Object.keys(LEGAL_TERMS_PRESETS).map(preset => (
                  <option key={preset} value={preset}>{preset}</option>
                ))}
              </select>
              <button
                onClick={handlePresetWarming}
                disabled={isWarming}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white rounded-lg transition-colors text-sm font-medium flex items-center space-x-1"
              >
                {isWarming ? (
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                ) : (
                  <SparklesIcon className="w-4 h-4" />
                )}
                <span>Warm</span>
              </button>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {LEGAL_TERMS_PRESETS[selectedPreset as keyof typeof LEGAL_TERMS_PRESETS]?.length || 0} terms
            </div>
          </div>

          {/* Custom Warming */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Custom Text (one per line)
            </label>
            <div className="flex space-x-2">
              <textarea
                value={customTexts}
                onChange={(e) => setCustomTexts(e.target.value)}
                placeholder="Enter custom words/phrases..."
                rows={3}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-transparent resize-none"
              />
              <button
                onClick={handleCustomWarming}
                disabled={isWarming || !customTexts.trim()}
                className="px-4 py-2 bg-lex-accent hover:bg-lex-accent-hover disabled:opacity-50 text-lex-accent-contrast rounded-lg transition-colors text-sm font-medium flex items-center space-x-1"
              >
                {isWarming ? (
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                ) : (
                  <FireIcon className="w-4 h-4" />
                )}
                <span>Warm</span>
              </button>
            </div>
          </div>
        </div>

        {/* Warming Results */}
        {warmingResults && (
          <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-800 dark:text-green-300">
                Cache Warming Complete
              </span>
            </div>
            <div className="text-sm text-green-700 dark:text-green-400">
              Warmed {warmingResults.warmedTexts} texts • 
              {warmingResults.results.successful} successful • 
              {warmingResults.results.cached} already cached
            </div>
          </div>
        )}
      </div>

      {/* Cache Entries */}
      <div className="p-6">
        {loading && (
          <div className="text-center py-8">
            <ArrowPathIcon className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-2" />
            <p className="text-gray-600 dark:text-gray-400">Loading cache data...</p>
          </div>
        )}

        {error && (
          <div className="flex items-center space-x-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
            <span className="text-red-700 dark:text-red-400">{error}</span>
          </div>
        )}

        {data && data.entries.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Showing {data.filteredEntries} of {data.totalCacheKeys} cached items
              {data.searchTerm && ` matching "${data.searchTerm}"`}
            </div>
            
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {data.entries.map((entry, index) => (
                <div
                  key={`${entry.key}-${index}`}
                  className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-sm text-gray-900 dark:text-white break-all">
                        "{entry.text}"
                      </div>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center space-x-1">
                          <LanguageIcon className="w-3 h-3" />
                          <span>{entry.fontFamily} {entry.fontSize}px</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <HashtagIcon className="w-3 h-3" />
                          <span>{entry.glyphCount} glyphs</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <ClockIcon className="w-3 h-3" />
                          <span>{new Date(entry.timestamp).toLocaleTimeString()}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {data && data.entries.length === 0 && !loading && (
          <div className="text-center py-8">
            <EyeIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {data.searchTerm ? `No cached items match "${data.searchTerm}"` : 'No cached items found'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
